import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { formatInTimeZone } from "date-fns-tz";
import LoadingButton from "components/LoadingButton";
import { getRunHistory } from "services/reportSchedulerService";
import { useSnackbar } from "hooks/useSnackbar";

const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  emptyContainer: {
    textAlign: "center",
    padding: "40px 0",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
}));

const STATUS_CHIP_COLORS: Record<
  string,
  "success" | "warning" | "error" | "default"
> = {
  success: "success",
  skipped: "warning",
  failed: "error",
};

const formatDateTime = (date?: string) => {
  if (!date) return "-";
  return formatInTimeZone(new Date(date), "UTC", "dd MMM yyyy HH:mm:ss 'UTC'");
};

const formatRecipients = (emails: string[]) => {
  if (!emails?.length) return "-";
  if (emails.length <= 2) return emails.join(", ");
  return `${emails.slice(0, 2).join(", ")} +${emails.length - 2} more`;
};

interface RunHistoryModalProps {
  open: boolean;
  scheduleId: string | null;
  onClose: () => void;
}

const RunHistoryModal: React.FC<RunHistoryModalProps> = ({
  open,
  scheduleId,
  onClose,
}) => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [runHistory, setRunHistory] = useState<any[]>([]);

  useEffect(() => {
    if (open && scheduleId) {
      fetchRunHistory(scheduleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId]);

  const fetchRunHistory = async (id: string) => {
    try {
      setLoading(true);
      setRunHistory([]);
      const response = await getRunHistory(id);
      if (response?.status && response?.data) {
        setRunHistory(response.data || []);
      }
    } catch (error: any) {
      console.error("Error fetching run history:", error);
      showSnackbar(error?.message || "Failed to fetch run history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRunHistory([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Run History
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : runHistory.length === 0 ? (
          <Box className={classes.emptyContainer}>
            <Typography variant="body1" color="text.secondary">
              No run history found for this schedule
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>
                    Run Time
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    Report Types
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    Date Range
                  </TableCell>
                  <TableCell className={classes.tableHeader} align="center">
                    Status
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    Reason / Row Count
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    Recipients
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runHistory.map((run: any, index: number) => (
                  <TableRow key={run._id || index} hover>
                    <TableCell>
                      {formatDateTime(run.runAt || run.createdAt)}
                    </TableCell>
                    <TableCell>
                      {run.reportTypes?.length
                        ? run.reportTypes
                            .map((t: string) =>
                              t === "daily_summary"
                                ? "Daily Summary"
                                : "Transactions",
                            )
                            .join(", ")
                        : run.reportType === "daily_summary"
                          ? "Daily Summary"
                          : run.reportType
                            ? "Transactions"
                            : "-"}
                    </TableCell>
                    <TableCell>
                      {run.dateRangeStart && run.dateRangeEnd
                        ? `${formatDateTime(run.dateRangeStart)} - ${formatDateTime(run.dateRangeEnd)}`
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={run.status}
                        color={STATUS_CHIP_COLORS[run.status] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {run.status === "success" && run.rowCounts
                        ? Object.entries(run.rowCounts)
                            .map(
                              ([type, count]) =>
                                `${type === "daily_summary" ? "Daily Summary" : "Transactions"}: ${count} rows`,
                            )
                            .join(" | ")
                        : run.status === "success" && run.rowCount != null
                          ? `${run.rowCount} rows`
                          : run.status === "skipped" || run.status === "failed"
                            ? run.reason || "-"
                            : "-"}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={run.recipientEmails?.join(", ") || ""}
                        placement="top"
                      >
                        <span>{formatRecipients(run.recipientEmails)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <LoadingButton onClick={handleClose} variant="outlined">
          Close
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RunHistoryModal;
